import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, alpha, Collapse } from '@mui/material';
import { CheckCircle, Warning, TrendingDown, ExpandMore, ExpandLess, ShowChart } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ── KPI cell ────────────────────────────────────
const KpiCell = ({ label, value, color, sub }) => (
  <Box sx={{ textAlign: 'center', flex: 1, py: 1.2, px: 1 }}>
    <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', mb: 0.2 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', mt: 0.15 }}>{sub}</Typography>}
  </Box>
);

// ── Score bar ────────────────────────────────────
const ScoreBar = ({ score, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ flex: 1, height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden', maxWidth: 50 }}>
      <Box sx={{ height: '100%', width: `${score}%`, bgcolor: color, borderRadius: 2, transition: 'width 0.3s' }} />
    </Box>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color, fontFamily: 'monospace', minWidth: 26 }}>
      {score?.toFixed(0)}
    </Typography>
  </Box>
);

// ── Stock table (CSS grid, shared for all signal types) ────────────
const StockTable = ({ stocks, color, icon, label, description, columns, t, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  
  if (!stocks || stocks.length === 0) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', mb: 0.5,
          px: 1, py: 0.4, borderRadius: 1,
          bgcolor: alpha(color, 0.06),
          '&:hover': { bgcolor: alpha(color, 0.1) }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {icon}
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{label}</Typography>
          <Chip label={stocks.length} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: color, color: '#fff', '& .MuiChip-label': { px: 0.5 } }} />
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{description}</Typography>
        </Box>
        {open ? <ExpandLess sx={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#94a3b8' }} />}
      </Box>

      <Collapse in={open}>
        <Box sx={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: columns.map(c => c.width).join(' '),
            p: '6px 12px',
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
            borderBottom: '1px solid #e2e8f0'
          }}>
            {columns.map((c, i) => (
              <Typography key={i} sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: '#64748b', textAlign: c.align || 'left' }}>
                {c.header}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {stocks.map((stock, idx) => (
            <Box key={idx} sx={{
              display: 'grid',
              gridTemplateColumns: columns.map(c => c.width).join(' '),
              p: '6px 12px',
              alignItems: 'center',
              borderBottom: '1px solid #f8fafc',
              borderLeft: `3px solid ${alpha(color, 0.5)}`,
              transition: 'background 0.15s',
              '&:hover': { background: alpha(color, 0.03) },
              ...(idx % 2 === 1 ? { bgcolor: '#fafbfc' } : {})
            }}>
              {columns.map((c, ci) => (
                <Box key={ci} sx={{ textAlign: c.align || 'left' }}>
                  {c.render(stock)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const StockCandidatesLayer = ({ stocksData, buyNowStocks, waitStocks, buyDipStocks, portfolioRisk, sectorsData }) => {
  const { t } = useTranslation();

  const allStocks = useMemo(() => [...buyNowStocks, ...(buyDipStocks || [])], [buyNowStocks, buyDipStocks]);
  const avgScore = allStocks.length > 0 ? allStocks.reduce((s, x) => s + (x.score || 0), 0) / allStocks.length : 0;
  const exposure = (stocksData?.exposure || 0) * 100;

  // Column definitions for BUY_NOW / BUY_DIP (same structure)
  const buyColumns = [
    { header: t('safeInvest.layer3.ticker'), width: '0.8fr', render: (s) => (
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
    )},
    { header: t('safeInvest.layer3.sector'), width: '1.2fr', render: (s) => (
      <Chip label={s.sector_name?.vi || s.sector_name?.en || s.sector} size="small"
        sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, bgcolor: alpha('#667eea', 0.08), color: '#667eea', '& .MuiChip-label': { px: 0.5 } }} />
    )},
    { header: t('safeInvest.layer3.score'), width: '1fr', render: (s) => <ScoreBar score={s.score} color={s.signal === 'BUY_NOW' ? '#10b981' : '#3b82f6'} /> },
    { header: t('safeInvest.layer3.weight'), width: '0.7fr', align: 'center', render: (s) => (
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>
        {(s.weight * 100).toFixed(1)}%
      </Typography>
    )},
    { header: t('safeInvest.layer3.entryReason'), width: '2fr', render: (s) => (
      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.3 }}>
        {s.reason?.vi || s.reason?.en || '—'}
      </Typography>
    )},
  ];

  // Column definitions for WAIT
  const waitColumns = [
    { header: t('safeInvest.layer3.ticker'), width: '0.8fr', render: (s) => (
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{s.ticker}</Typography>
    )},
    { header: t('safeInvest.layer3.sector'), width: '1.2fr', render: (s) => (
      <Chip label={s.sector_name?.vi || s.sector_name?.en || s.sector} size="small"
        sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, bgcolor: alpha('#667eea', 0.08), color: '#667eea', '& .MuiChip-label': { px: 0.5 } }} />
    )},
    { header: t('safeInvest.layer3.score'), width: '1fr', render: (s) => <ScoreBar score={s.score} color="#f59e0b" /> },
    { header: t('safeInvest.layer3.status'), width: '1fr', align: 'center', render: (s) => (
      <Chip
        label={s.status?.vi || s.status?.en || t('safeInvest.layer3.waitCount')}
        icon={<ShowChart sx={{ fontSize: 10 }} />}
        size="small"
        sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', border: `1px solid ${alpha('#f59e0b', 0.2)}`, '& .MuiChip-label': { px: 0.5 } }}
      />
    )},
    { header: t('safeInvest.layer3.waitReason'), width: '2fr', render: (s) => (
      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.3 }}>
        {s.reason?.vi || s.reason?.en || '—'}
      </Typography>
    )},
  ];

  return (
    <Box sx={{ 
      background: '#fff', 
      border: '1px solid #e5e7eb', 
      borderRadius: 2.5, 
      overflow: 'hidden',
      mt: 2
    }}>
      {/* ═══════ KPI STRIP (merged from PortfolioVisualization) ═══════ */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
        <KpiCell label="Tổng CP" value={allStocks.length} color="#667eea" sub={`${buyNowStocks.length} buy · ${buyDipStocks.length} dip`} />
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9', my: 0.5 }} />
        <KpiCell label="Chất lượng TB" value={`${avgScore.toFixed(0)}`} color="#3b82f6" sub="/100" />
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9', my: 0.5 }} />
        <KpiCell label="Exposure" value={`${exposure.toFixed(0)}%`} color="#f59e0b" />
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9', my: 0.5 }} />
        <KpiCell
          label="Rủi ro"
          value={`${portfolioRisk.toFixed(1)}%`}
          color={portfolioRisk < 15 ? '#10b981' : portfolioRisk < 30 ? '#f59e0b' : '#ef4444'}
          sub={portfolioRisk < 15 ? 'Thấp' : portfolioRisk < 30 ? 'TB' : 'Cao'}
        />
        <Box sx={{ width: '1px', bgcolor: '#f1f5f9', my: 0.5 }} />
        <KpiCell label="Chờ PB" value={waitStocks.length} color="#d97706" sub="WAIT" />
      </Box>

      {/* ═══════ STOCK TABLES ═══════ */}
      <Box sx={{ p: 1.5 }}>
        {/* BUY NOW */}
        <StockTable
          stocks={buyNowStocks}
          color="#10b981"
          icon={<CheckCircle sx={{ fontSize: 14, color: '#10b981' }} />}
          label={t('safeInvest.layer3.buyNow')}
          description={t('safeInvest.layer3.buyNowDesc')}
          columns={buyColumns}
          t={t}
          defaultOpen={true}
        />

        {/* WAIT PULLBACK */}
        <StockTable
          stocks={waitStocks}
          color="#f59e0b"
          icon={<Warning sx={{ fontSize: 14, color: '#f59e0b' }} />}
          label={t('safeInvest.layer3.wait')}
          description={t('safeInvest.layer3.waitDesc')}
          columns={waitColumns}
          t={t}
          defaultOpen={false}
        />

        {/* BUY DIP */}
        <StockTable
          stocks={buyDipStocks}
          color="#3b82f6"
          icon={<TrendingDown sx={{ fontSize: 14, color: '#3b82f6' }} />}
          label={t('safeInvest.layer3.buyDipFull', 'BUY DIP')}
          description=""
          columns={buyColumns}
          t={t}
          defaultOpen={false}
        />
      </Box>
    </Box>
  );
};

export default StockCandidatesLayer;
